import React from 'react';
import {
  Button,
  FormControl,
  FormHelperText,
  MenuItem,
  Typography,
} from '@mui/material';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import {
  ProductInformationProps,
  StyledTextField,
} from '../productInformation';
import { set } from 'lodash';
import { useQuery } from 'react-query';
import { client } from 'src/utils/api-client';
import { useAllCountries } from 'src/common/hooks';
import { useTranslation } from 'react-i18next';
import {
  getProductComparableFieldValue,
  useProductFieldError,
} from '../validation';
import { buildSelectInputWithInfo } from '../fieldInfo';
import { ComplianceField } from 'src/Lca/compliance';

type LcaRegion = {
  iso_code: string;
  name: string;
};

type ProducerValues = {
  name: string;
  address: string;
  website: string;
};

const parseProducer = (rawValue: string): ProducerValues => {
  const fallback: ProducerValues = {
    name: '',
    address: '',
    website: '',
  };
  if (!rawValue) {
    return fallback;
  }

  const sections = rawValue
    .split('|')
    .map((section) => section.trim())
    .filter(Boolean);

  for (const section of sections) {
    const [left, ...rightParts] = section.split(':');
    const right = rightParts.join(':').trim();
    const key = left.toLowerCase().trim();

    if (key.includes('name') || key.includes('producer')) {
      fallback.name = right;
    } else if (key.includes('address')) {
      fallback.address = right;
    } else if (key.includes('website')) {
      fallback.website = right;
    }
  }

  if (!fallback.name && !fallback.address && !fallback.website) {
    fallback.name = rawValue;
  }

  return fallback;
};

const toProducer = (values: ProducerValues) =>
  [
    values.name && `Name: ${values.name}`,
    values.address && `Address: ${values.address}`,
    values.website && `Website: ${values.website}`,
  ]
    .filter(Boolean)
    .join(' | ');

export default function Location({
  setValues,
  values,
}: ProductInformationProps) {
  const { t, i18n } = useTranslation('lca', { keyPrefix: 'location' });
  const handleLocation = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setValues({ ...values, production_location: e.target.value });
  };
  const handleProducerActivity = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setValues({ ...values, key_company_activities: e.target.value });
  };
  const handleImporterName = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setValues({ ...values, importer_name: e.target.value });
  };

  const handleRegion = (e: { target: { value: string } }, i: number) => {
    const temp = { ...values };
    set(temp, `distribution_regions[${i}].iso_code`, e.target.value);
    setValues(temp);
  };

  const handleCountry = (e: { target: { value: string } }) => {
    setValues({ ...values, production_country: { iso_code: e.target.value } });
  };

  const addRegion = () => {
    setValues({
      ...values,
      distribution_regions: [
        ...values.distribution_regions,
        { iso_code: '', name: '' },
      ],
    });
  };

  const removeRegion = (i: number) => {
    setValues({
      ...values,
      distribution_regions: values.distribution_regions.filter(
        (_: any, index: number) => index !== i
      ),
    });
  };

  const { data: regionsData } = useQuery('getRegions', () => {
    return client.get<LcaRegion[]>('/web/lca/regions');
  });

  const { data: countriesData } = useAllCountries();
  const parsedProducer = parseProducer(values.manufacturer_name || '');
  const productionCountryError = useProductFieldError(
    'production_country',
    getProductComparableFieldValue(values as any, 'production_country')
  );
  const distributionRegionsError = useProductFieldError(
    'distribution_regions',
    getProductComparableFieldValue(values as any, 'distribution_regions')
  );

  const updateProducer = (patch: Partial<ProducerValues>) => {
    const merged = {
      ...parsedProducer,
      ...patch,
    };
    setValues({
      ...values,
      manufacturer_name: toProducer(merged),
    });
  };

  return (
    <div
      style={{
        display: 'flex',
        gap: '24px',
        flexDirection: 'column',
      }}
    >
      <Typography sx={{ fontWeight: 700, color: '#281302' }}>
        {t('producerSectionTitle')}
      </Typography>
      <ComplianceField
        excelRow={10}
        regulatoryFrameworks={values.regulatory_frameworks}
      >
        <StyledTextField
          fullWidth
          label={t('producerName')}
          value={parsedProducer.name}
          onChange={(e) => updateProducer({ name: e.target.value })}
        />
      </ComplianceField>
      <ComplianceField
        excelRow={11}
        regulatoryFrameworks={values.regulatory_frameworks}
      >
        <StyledTextField
          fullWidth
          label={t('producerAddress')}
          value={parsedProducer.address}
          onChange={(e) => updateProducer({ address: e.target.value })}
          multiline
          rows={2}
        />
      </ComplianceField>
      <ComplianceField
        excelRow={12}
        regulatoryFrameworks={values.regulatory_frameworks}
      >
        <StyledTextField
          fullWidth
          label={t('producerWebsite')}
          value={parsedProducer.website}
          onChange={(e) => updateProducer({ website: e.target.value })}
        />
      </ComplianceField>
      <ComplianceField
        excelRow={13}
        regulatoryFrameworks={values.regulatory_frameworks}
      >
        <StyledTextField
          fullWidth
          label={t('producerActivityDescription')}
          value={values.key_company_activities}
          onChange={handleProducerActivity}
          multiline
          rows={2}
        />
      </ComplianceField>
      <StyledTextField
        fullWidth
        label={i18n.t('lca:basicInfo.importerName')}
        value={values.importer_name}
        onChange={handleImporterName}
      />

      <Typography sx={{ fontWeight: 700, color: '#281302' }}>
        {t('countryOfProduction')}
      </Typography>
      <ComplianceField
        excelRow={14}
        regulatoryFrameworks={values.regulatory_frameworks}
      >
        <FormControl fullWidth error={!!productionCountryError}>
          <InputLabel>{t('countryOfProduction')}</InputLabel>
          <Select
            value={values.production_country.iso_code}
            label={t('countryOfProduction')}
            input={buildSelectInputWithInfo({
              label: t('countryOfProduction'),
              title: i18n.t('lca:fieldInfoFallback', {
                field: t('countryOfProduction'),
              }),
            })}
            onChange={(event) => handleCountry(event)}
          >
            {countriesData.map((el: { iso_code: string; name: string }) => (
              <MenuItem key={el.iso_code} value={el.iso_code}>
                {el.name}
              </MenuItem>
            ))}
          </Select>
          {productionCountryError && (
            <FormHelperText>{productionCountryError}</FormHelperText>
          )}
        </FormControl>
      </ComplianceField>
      <ComplianceField
        excelRow={15}
        regulatoryFrameworks={values.regulatory_frameworks}
      >
        <StyledTextField
          fullWidth
          label={t('locationOfProduction')}
          name="production_location"
          value={values.production_location}
          onChange={handleLocation}
        />
      </ComplianceField>

      <Typography sx={{ fontWeight: 700, color: '#281302' }}>
        {t('distributionSectionTitle')}
      </Typography>
      {values.distribution_regions.map((_: any, i: number) => (
        <div
          key={i}
          style={{
            display: 'flex',
            gap: '24px',
            alignItems: 'center',
          }}
        >
          <ComplianceField
            excelRow={16}
            regulatoryFrameworks={values.regulatory_frameworks}
          >
            <FormControl fullWidth error={!!distributionRegionsError}>
              <InputLabel>{t('distributionRegion')}</InputLabel>
              <Select
                value={values.distribution_regions[i].iso_code}
                label={t('distributionRegion')}
                input={buildSelectInputWithInfo({
                  label: t('distributionRegion'),
                  title: i18n.t('lca:fieldInfoFallback', {
                    field: t('distributionRegion'),
                  }),
                })}
                onChange={(event) => handleRegion(event, i)}
              >
                {regionsData?.data.map(
                  (el: { iso_code: string; name: string }) => (
                    <MenuItem key={el.iso_code} value={el.iso_code}>
                      {el.name}
                    </MenuItem>
                  )
                )}
              </Select>
              {i === 0 && distributionRegionsError && (
                <FormHelperText>{distributionRegionsError}</FormHelperText>
              )}
            </FormControl>
          </ComplianceField>
          <Button
            size="small"
            variant="secondary"
            disabled={values.distribution_regions[i].iso_code === ''}
            onClick={() => removeRegion(i)}
          >
            {t('remove')}
          </Button>
        </div>
      ))}
      <div>
        <Button
          variant="secondary"
          size="small"
          onClick={addRegion}
          disabled={
            values.distribution_regions[values.distribution_regions.length - 1]
              .iso_code === ''
          }
        >
          {t('addDistributionRegion')}
        </Button>
      </div>
    </div>
  );
}
